import { useMetaData } from './useMetaData.js'
import { useUpdateMeta } from '../mutations/useUpdateMeta.js'
import { useUIStore } from '../store/uiStore.js'

/**
 * Key for a DM thread — sorted participant names joined by commas.
 * This guarantees "Ricky,Katie" and "Katie,Ricky" resolve to the same thread.
 */
export function dmKey(participants) {
  return [...participants].sort().join(',')
}

/**
 * useDms — read + mutate direct-message threads.
 *
 * Threads live under meta.dms, keyed by sorted-participant-list. Each thread
 * has { participants, messages }. A message is { id, author, text, at }.
 *
 * Pass `participants` = array of OTHER participants (not including the
 * current user — that's added automatically). For a 1-on-1 with Katie,
 * pass `['Katie']`.
 */
export function useDms(otherParticipants) {
  const currentUser = useUIStore((s) => s.currentUser) || 'Unknown'
  const dmSeen = useUIStore((s) => s.dmSeen)
  const markSeen = useUIStore((s) => s.markDmSeen)
  const { data: meta } = useMetaData()
  const updateMeta = useUpdateMeta()

  const participants = otherParticipants ? [currentUser, ...otherParticipants] : []
  const key = participants.length > 1 ? dmKey(participants) : null
  const thread = (key && meta.dms?.[key]) || null
  const messages = thread?.messages || []

  const newId = () =>
    Date.now().toString(36) + Math.random().toString(36).slice(2, 5)

  const sendMessage = (text) => {
    if (!key || !text?.trim()) return
    const msg = {
      id: newId(),
      author: currentUser,
      text: text.trim(),
      at: new Date().toISOString(),
    }
    updateMeta.mutate((m) => {
      const prevMsgs = m.dms?.[key]?.messages || []
      const newMsgs = [...prevMsgs, msg]
      return {
        ...m,
        dms: {
          ...m.dms,
          [key]: { participants, messages: newMsgs },
        },
      }
    })
    // Our own sent messages are considered seen immediately
    markSeen(key, (thread?.messages?.length || 0) + 1)
  }

  const seenCount = key ? dmSeen[key] || 0 : 0
  const unreadCount = Math.max(
    0,
    messages.filter((m) => m.author !== currentUser).length -
      messages.slice(0, seenCount).filter((m) => m.author !== currentUser).length
  )

  const markAsRead = () => {
    if (!key) return
    markSeen(key, messages.length)
  }

  return {
    thread,
    key,
    messages,
    sendMessage,
    unreadCount,
    markAsRead,
  }
}

/**
 * useAllDms — read-only list of every thread the current user is part of.
 * Useful for a badge-count summary or a future inbox view.
 */
export function useAllDms() {
  const currentUser = useUIStore((s) => s.currentUser)
  const dmSeen = useUIStore((s) => s.dmSeen)
  const { data: meta } = useMetaData()

  if (!currentUser) return { threads: [], totalUnread: 0 }

  const myThreads = Object.entries(meta.dms || {})
    .filter(([, t]) => (t?.participants || []).includes(currentUser))
    .map(([key, t]) => {
      const other = (t.participants || []).filter((p) => p !== currentUser)
      const seen = dmSeen[key] || 0
      const msgs = t.messages || []
      const unread = Math.max(
        0,
        msgs.filter((m) => m.author !== currentUser).length -
          msgs.slice(0, seen).filter((m) => m.author !== currentUser).length
      )
      return { key, thread: t, other, unread, lastAt: msgs[msgs.length - 1]?.at }
    })
    .sort((a, b) => (b.lastAt || '').localeCompare(a.lastAt || ''))

  const totalUnread = myThreads.reduce((n, t) => n + t.unread, 0)

  return { threads: myThreads, totalUnread }
}
